import React from 'react'
import { hashHistory } from 'react-router'
import {
  CircularProgress,
  Grid,
  Box,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import GroupDocuments from './GroupDocuments'
import GroupPolicies from './GroupPolicies'
import {
  CardHeader,
  Field,
  StatusPill,
  detailCard,
  labelStyle,
  rowStyle,
  safeText,
  titleCase,
  valueStyle
} from './ui'

const NAV_ITEMS = [
  'Overview',
  'Contact Person',
  'Product & Cover',
  'Facilitators',
  'Member Policies',
  'Documents',
  'Verifications'
]

const VERIFICATION_STATUSES = [
  'pending',
  'passed',
  'failed',
  'not_required',
  'flagged_for_review'
]

const headerCard = {
  background: '#FFFFFF',
  borderRadius: '8px',
  padding: '16px 20px'
}

const navCard = {
  background: '#FFFFFF',
  borderRadius: '8px',
  padding: '20px',
  marginTop: '16px'
}

const verticalTabsSx = {
  borderRight: 1,
  borderColor: 'divider',
  minWidth: '190px',
  '& .MuiTabs-indicator': {
    backgroundColor: '#032A37'
  },
  '& .MuiTab-root': {
    fontFamily: 'Open Sans',
    textTransform: 'capitalize',
    fontSize: '13px',
    color: '#000000',
    alignItems: 'flex-start',
    textAlign: 'left',
    minHeight: '44px'
  },
  '& .MuiTab-root.Mui-selected': {
    color: '#000',
    background: '#F8FAFC',
    fontWeight: 'bold',
    borderRadius: '8px'
  }
}

class GroupDetail extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      navValue: 0,
      approveDialogOpen: false,
      verificationDialogOpen: false,
      selectedVerificationType: '',
      verificationStatus: 'Verified',
      verificationNotes: ''
    }
    this.handleApprove = this.handleApprove.bind(this)
    this.handleSetVerification = this.handleSetVerification.bind(this)
    this.openVerificationDialog = this.openVerificationDialog.bind(this)
    this.closeVerificationDialog = this.closeVerificationDialog.bind(this)
    this.handleNavChange = this.handleNavChange.bind(this)
    this.renderPanel = this.renderPanel.bind(this)
    this.renderIssuanceStatus = this.renderIssuanceStatus.bind(this)
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.approve_group_success && this.props.approve_group_success) {
      this.props.resetApprove()
    }
    if (
      !prevProps.set_verification_success &&
      this.props.set_verification_success
    ) {
      this.props.resetVerification()
    }
  }

  handleNavChange(event, newValue) {
    this.setState({ navValue: newValue })
  }

  handleApprove() {
    const group = this.props.group
    if (group) {
      this.setState({ approveDialogOpen: false })
      this.props.approveGroup(group.guid)
    }
  }

  openVerificationDialog(verificationType) {
    this.setState({
      verificationDialogOpen: true,
      selectedVerificationType: verificationType,
      verificationStatus: 'passed',
      verificationNotes: ''
    })
  }

  closeVerificationDialog() {
    this.setState({
      verificationDialogOpen: false,
      selectedVerificationType: ''
    })
  }

  handleSetVerification() {
    const group = this.props.group
    if (!group) return
    const payload = {
      guid: group.guid,
      verificationType: this.state.selectedVerificationType,
      status: this.state.verificationStatus,
      notes: this.state.verificationNotes
    }
    this.closeVerificationDialog()
    this.props.setVerification(payload)
  }

  renderOverview(group) {
    return (
      <div style={detailCard}>
        <CardHeader title={safeText(group.group_name)} status={group.status} />
        <Grid container spacing={2}>
          <Field label="Group Number" value={group.group_number} />
          <Field label="Group Type" value={titleCase(group.group_type)} />
          <Field label="Category" value={titleCase(group.group_category)} />
          <Field label="Estimated Size" value={group.estimated_group_size} />
          <Field
            label="Product Code"
            value={group.product_code}
            sm={8}
            md={8}
          />
          <Field
            label="Reg. Certificate No."
            value={group.registration_certificate_number}
          />
          <Field
            label="Data Protection Consent"
            value={group.data_protection_consent}
          />
          <Field
            label="Policies Registered"
            value={group.policies_registered}
          />
          <Field label="Documents Complete" value={group.docs_complete} />
        </Grid>
      </div>
    )
  }

  renderContact(group) {
    const contact = group.primary_contact_person || {}
    return (
      <div style={detailCard}>
        <CardHeader title="Primary Contact Person" />
        <Grid container spacing={2}>
          <Field label="Name" value={contact.name} sm={4} md={4} />
          <Field
            label="Mobile Number"
            value={contact.mobile_number}
            sm={4}
            md={4}
          />
          <Field label="Email" value={contact.email} sm={4} md={4} />
        </Grid>
      </div>
    )
  }

  renderProduct(group) {
    const configs = group.group_product_configs || {}
    const details = group.additional_details || {}
    const waitingPeriods = configs.waiting_periods || []
    return (
      <div>
        <div style={detailCard}>
          <CardHeader title="Cover Details" />
          <Grid container spacing={2}>
            <Field label="Benefit Amount" value={details.benefit_amount} />
            <Field label="Claim Plan" value={titleCase(details.claim_plan)} />
            <Field label="Group KRA PIN" value={details.group_kra_pin} />
          </Grid>
        </div>

        <div style={detailCard}>
          <CardHeader title="Product Configuration" />
          <Grid container spacing={2}>
            <Field
              label="Min Entry Age"
              value={configs.principal_entry_minimum_age}
            />
            <Field
              label="Max Entry Age"
              value={configs.principal_entry_maximum_age}
            />
            <Field
              label="Max Lives / Policy"
              value={configs.max_lives_per_policy}
            />
            <Field
              label="Minimum Group Size"
              value={configs.minimum_group_size}
            />
            <Field label="Max Children" value={configs.maximum_children} />
            <Field label="Max Parents" value={configs.maximum_parents} />
            <Field
              label="Max Parents-in-law"
              value={configs.maximum_parents_in_laws}
            />
          </Grid>

          {waitingPeriods.length > 0 && (
            <div>
              <p
                style={Object.assign({}, labelStyle, { margin: '18px 0 8px' })}
              >
                Waiting Periods
              </p>
              {waitingPeriods.map(function(wp, i) {
                return (
                  <Box
                    key={i}
                    mb={1}
                    p={1.5}
                    style={rowStyle}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <h6 style={valueStyle}>{titleCase(wp.benefit_type)}</h6>
                    <span style={valueStyle}>
                      {wp.waiting_period_cardinality}{' '}
                      {titleCase(wp.waiting_period_granularity)}
                    </span>
                  </Box>
                )
              })}
            </div>
          )}
        </div>
      </div>
    )
  }

  renderFacilitators(group) {
    const facilitators = group.facilitators || []
    return (
      <div style={detailCard}>
        <CardHeader title="Facilitators" />
        {facilitators.length === 0 ? (
          <p style={labelStyle}>No facilitators assigned.</p>
        ) : (
          facilitators.map(function(f, i) {
            return (
              <Box
                key={i}
                mb={1}
                p={1.5}
                style={rowStyle}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <div>
                  <h6 style={valueStyle}>
                    {safeText(f.facilitator_name || f.full_name)}
                  </h6>
                  <p style={labelStyle}>
                    {safeText(f.mobile_number || f.facilitator_guid)}
                  </p>
                </div>
                {f.primary_facilitator && <StatusPill status="Primary" />}
              </Box>
            )
          })
        )}
      </div>
    )
  }

  // Approving a group now also issues its members' policies, so the operator
  // is told what happened to that second step rather than being left to guess
  // whether they still need to go and issue them by hand.
  renderIssuanceStatus() {
    const state = this.props.issue_policies_state
    if (!state) {
      return null
    }
    const queued = this.props.issue_policies_queued
    const skipped = this.props.issue_policies_skipped
    const counts = []
    if (typeof queued === 'number') {
      counts.push(`${queued} queued`)
    }
    if (typeof skipped === 'number' && skipped > 0) {
      counts.push(`${skipped} skipped because they failed validation`)
    }
    const tail = counts.length ? ` (${counts.join(', ')})` : ''

    let severity = 'info'
    let message

    if (state === 'issuing') {
      message = `Issuing member policies…${tail}`
    } else if (state === 'registered') {
      severity = 'success'
      message = `Member policies issued${tail}.`
    } else if (state === 'pending') {
      // Queued work that has not finished yet is normal — say so plainly
      // instead of implying something went wrong.
      message =
        `Member policies are still being issued${tail}. ` +
        'They will appear under Member Policies as they complete.'
    } else {
      severity = 'error'
      message = `The group was approved, but issuing its policies failed: ${safeText(
        this.props.issue_policies_error
      )}`
    }

    return (
      <Alert
        severity={severity}
        style={{ marginTop: '16px', borderRadius: '8px' }}
      >
        {message}
      </Alert>
    )
  }

  renderPolicies(group) {
    return (
      <GroupPolicies
        group={group}
        policies={this.props.group_policies}
        total={this.props.group_policies_total}
        page={this.props.group_policies_page}
        limit={this.props.group_policies_limit}
        loader={this.props.group_policies_loader}
        error={this.props.group_policies_error}
        getPolicies={this.props.getPolicies}
        approvePolicies={this.props.approvePolicies}
        resetApprovePolicies={this.props.resetApprovePolicies}
        approve_policies_loader={this.props.approve_policies_loader}
        approve_policies_success={this.props.approve_policies_success}
        approve_policies_error={this.props.approve_policies_error}
        approve_policies_total={this.props.approve_policies_total}
        approve_policies_results={this.props.approve_policies_results}
        approve_policies_decision={this.props.approve_policies_decision}
        issue_policies_state={this.props.issue_policies_state}
      />
    )
  }

  renderDocuments(group) {
    return (
      <GroupDocuments
        group={group}
        uploadDocument={this.props.uploadDocument}
        deleteDocument={this.props.deleteDocument}
        resetUploadDocument={this.props.resetUploadDocument}
        resetDeleteDocument={this.props.resetDeleteDocument}
        upload_document_loader={this.props.upload_document_loader}
        upload_document_success={this.props.upload_document_success}
        upload_document_error={this.props.upload_document_error}
        delete_document_loader={this.props.delete_document_loader}
        delete_document_success={this.props.delete_document_success}
        delete_document_error={this.props.delete_document_error}
      />
    )
  }

  renderVerifications(group) {
    const verifications = group.verifications || []
    const requiredVerifications = group.required_verifications || []
    return (
      <div style={detailCard}>
        <CardHeader title="Verifications" />
        {requiredVerifications.length === 0 ? (
          <p style={labelStyle}>No verifications required.</p>
        ) : (
          requiredVerifications.map(
            function(verItem) {
              const verType =
                verItem && typeof verItem === 'object' ? verItem.type : verItem
              const verObj =
                verifications.find(function(v) {
                  return v.type === verType
                }) || {}
              const verStatus = verObj.status || 'pending'
              return (
                <Box
                  key={verType}
                  mb={1}
                  p={1.5}
                  style={rowStyle}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <div>
                    <h6 style={valueStyle}>{titleCase(verType)}</h6>
                    <Box mt={0.5}>
                      <StatusPill status={verStatus} />
                    </Box>
                    {verObj.notes && (
                      <p
                        style={Object.assign({}, labelStyle, { marginTop: 4 })}
                      >
                        {safeText(verObj.notes)}
                      </p>
                    )}
                  </div>
                  <ButtonInc
                    btnType="secondary"
                    onClick={function() {
                      this.openVerificationDialog(verType)
                    }.bind(this)}
                  >
                    Set Status
                  </ButtonInc>
                </Box>
              )
            }.bind(this)
          )
        )}
      </div>
    )
  }

  renderPanel(group) {
    const navValue = this.state.navValue
    if (navValue === 0) return this.renderOverview(group)
    if (navValue === 1) return this.renderContact(group)
    if (navValue === 2) return this.renderProduct(group)
    if (navValue === 3) return this.renderFacilitators(group)
    if (navValue === 4) return this.renderPolicies(group)
    if (navValue === 5) return this.renderDocuments(group)
    return this.renderVerifications(group)
  }

  render() {
    const group = this.props.group

    if (this.props.loader) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
          p={6}
        >
          <CircularProgress style={{ color: '#032A37' }} />
          <span style={labelStyle}>Loading group details…</span>
        </Box>
      )
    }

    if (this.props.error) {
      return (
        <div>
          <Alert severity="error" style={{ borderRadius: '8px' }}>
            {safeText(this.props.error)}
          </Alert>
          <Box mt={2}>
            <ButtonInc
              btnType="secondary"
              startIcon={<ArrowBackIcon />}
              onClick={function() {
                hashHistory.push('admin/group-dashboard')
              }}
            >
              Back to Groups
            </ButtonInc>
          </Box>
        </div>
      )
    }

    if (!group) return null

    const canApprove =
      group.status !== 'endorsed' && group.status !== 'cancelled'

    return (
      <div>
        <Box mb={2}>
          <ButtonInc
            btnType="secondary"
            startIcon={<ArrowBackIcon style={{ fontSize: '16px' }} />}
            onClick={function() {
              hashHistory.push('admin/group-dashboard')
            }}
          >
            Back to Groups
          </ButtonInc>
        </Box>

        {/* Header card */}
        <section style={headerCard}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <div
                style={{
                  width: '34px',
                  height: '34px',
                  background: '#032A37',
                  borderRadius: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <GroupsOutlinedIcon
                  style={{ color: '#ffffff', fontSize: '18px' }}
                />
              </div>
              <div>
                <h1
                  style={{
                    fontFamily: 'Lato',
                    fontWeight: 500,
                    fontSize: '16px',
                    color: '#000000',
                    margin: 0
                  }}
                >
                  Group Validation
                </h1>
                <span style={labelStyle}>
                  {safeText(group.group_name)} · {safeText(group.group_number)}
                </span>
              </div>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <StatusPill status={group.status} />
              {canApprove && (
                <ButtonInc
                  variant="contained"
                  onClick={function() {
                    this.setState({ approveDialogOpen: true })
                  }.bind(this)}
                  loading={this.props.approve_group_loader}
                  disabled={this.props.approve_group_loader}
                >
                  Approve Group
                </ButtonInc>
              )}
            </Box>
          </Box>
        </section>

        {this.props.approve_group_error && (
          <Alert
            severity="error"
            style={{ marginTop: '16px', borderRadius: '8px' }}
          >
            {safeText(this.props.approve_group_error)}
          </Alert>
        )}

        {this.renderIssuanceStatus()}
        {this.props.set_verification_error && (
          <Alert
            severity="error"
            style={{ marginTop: '16px', borderRadius: '8px' }}
          >
            {safeText(this.props.set_verification_error)}
          </Alert>
        )}

        {/* Left-nav + content */}
        <section style={navCard}>
          <Box sx={{ display: 'flex', minHeight: '320px' }}>
            <Tabs
              orientation="vertical"
              value={this.state.navValue}
              onChange={this.handleNavChange}
              aria-label="group detail sections"
              sx={verticalTabsSx}
            >
              {NAV_ITEMS.map(function(label, i) {
                return <Tab key={i} label={label} />
              })}
            </Tabs>
            {/* minWidth: 0 overrides the flex item's min-width:auto, which
                would otherwise stretch this column to its widest content and
                push the whole page into a horizontal scroll. With it, a wide
                panel (the Member Policies table) scrolls inside itself. */}
            <Box
              sx={{
                flexGrow: 1,
                minWidth: 0,
                paddingLeft: '24px',
                width: '100%'
              }}
            >
              {this.renderPanel(group)}
            </Box>
          </Box>
        </section>

        {/* Approve dialog */}
        <Dialog
          open={this.state.approveDialogOpen}
          onClose={function() {
            this.setState({ approveDialogOpen: false })
          }.bind(this)}
          PaperProps={{ style: { borderRadius: '8px', padding: '8px' } }}
        >
          <DialogTitle style={{ fontFamily: 'Lato', fontWeight: 600 }}>
            Approve Group
          </DialogTitle>
          <DialogContent>
            <span style={{ fontFamily: 'Open Sans', fontSize: '13px' }}>
              Are you sure you want to approve{' '}
              <strong>{safeText(group.group_name)}</strong>? This will advance
              the group status to Endorsed.
            </span>
          </DialogContent>
          <DialogActions style={{ padding: '16px' }}>
            <ButtonInc
              btnType="secondary"
              onClick={function() {
                this.setState({ approveDialogOpen: false })
              }.bind(this)}
              disabled={this.props.approve_group_loader}
            >
              Cancel
            </ButtonInc>
            <ButtonInc
              variant="contained"
              onClick={this.handleApprove}
              loading={this.props.approve_group_loader}
              disabled={this.props.approve_group_loader}
            >
              Confirm Approve
            </ButtonInc>
          </DialogActions>
        </Dialog>

        {/* Verification dialog */}
        <Dialog
          open={this.state.verificationDialogOpen}
          onClose={this.closeVerificationDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{ style: { borderRadius: '8px', padding: '8px' } }}
        >
          <DialogTitle style={{ fontFamily: 'Lato', fontWeight: 600 }}>
            Set Verification: {titleCase(this.state.selectedVerificationType)}
          </DialogTitle>
          <DialogContent>
            <Box mt={1} display="flex" flexDirection="column" gap={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Verification Status</InputLabel>
                <Select
                  value={this.state.verificationStatus}
                  label="Verification Status"
                  onChange={function(e) {
                    this.setState({ verificationStatus: e.target.value })
                  }.bind(this)}
                >
                  {VERIFICATION_STATUSES.map(function(s) {
                    return (
                      <MenuItem key={s} value={s}>
                        {titleCase(s)}
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>
              <TextField
                label="Notes (optional)"
                value={this.state.verificationNotes}
                onChange={function(e) {
                  this.setState({ verificationNotes: e.target.value })
                }.bind(this)}
                multiline
                rows={3}
                fullWidth
                size="small"
              />
            </Box>
          </DialogContent>
          <DialogActions style={{ padding: '16px' }}>
            <ButtonInc
              btnType="secondary"
              onClick={this.closeVerificationDialog}
              disabled={this.props.set_verification_loader}
            >
              Cancel
            </ButtonInc>
            <ButtonInc
              variant="contained"
              onClick={this.handleSetVerification}
              loading={this.props.set_verification_loader}
              disabled={this.props.set_verification_loader}
            >
              Save
            </ButtonInc>
          </DialogActions>
        </Dialog>
      </div>
    )
  }
}

export default GroupDetail
